import cuid from "cuid";
import * as Device from "expo-device";
import Constants from "expo-constants";
import { Dimensions, Platform, AppState } from "react-native";

import * as Localization from "expo-localization";
import * as Sentry from "@sentry/react-native";
import { addBreadcrumb } from "../sentry";
import { sessionId } from "../../OS/sessionId";
import { getDeviceId } from "./getDeviceId";

const screenWidth = Dimensions.get("screen").width;
const screenHeight = Dimensions.get("screen").height;

let deviceType: Device.DeviceType | undefined = undefined;
let maxMem: number | undefined = undefined;

const percentOfSessionToTrackPageViews = 20;
const shouldTrackPageViews =
  Math.random() < percentOfSessionToTrackPageViews / 100;
export class StoresprintTracking {
  queue: {
    event: string;
    properties: { time: number; $insert_id: string; distinct_id?: string };
    userProperties: object;
  }[] = [];
  state: "idle" | "flushing" = "idle";
  mixpanelToken?: string;

  timeout?: NodeJS.Timeout;
  distinct_id?: string;
  store_id?: string;
  org_id?: string;
  device_id?: string;
  is_storesprint_user?: boolean;

  constructor(mixpanelToken?: string) {
    this.mixpanelToken = mixpanelToken;

    AppState.addEventListener("change", (nextAppState) => {
      if (nextAppState === "background" || nextAppState === "inactive") {
        this.flush();
      }
    });

    getDeviceId().then((id) => {
      this.device_id = id;
    });
  }

  identify(
    id: string,
    scope: { id: string; type: "store" } | { id: string; type: "org" },
    isStoresprintUser?: boolean
  ) {
    this.distinct_id = id;
    this.store_id =
      scope.type === "store" ? scope.id.replace("SPS_", "") : undefined;
    this.org_id = scope.type === "org" ? scope.id : undefined;
    this.is_storesprint_user = isStoresprintUser;

    if (scope.type === "store") {
      Sentry.setTag("storeId", scope.id.replace("SPS_", ""));
    }
  }

  tick() {
    if (this.timeout) {
      clearTimeout(this.timeout);
      if (this.queue.length > 20) {
        this.flush();
      }
    }
    if (this.state !== "flushing" && this.queue.length > 0) {
      this.timeout = setTimeout(() => {
        this.flush();
      }, 15000);
    }
  }

  _track(event: "Mobile Page View", userProperties: object) {
    if (this.queue.length > 300) {
      // we have not been able to flush theese.
      // clear! we dont want any mem-leaks
      this.queue = [];
    }
    const insert_id = cuid();

    if (this.mixpanelToken) {
      this.queue.push({
        event,
        properties: {
          $insert_id: insert_id,
          time: Date.now(),
          distinct_id: this.distinct_id,
        },
        userProperties,
      });
    }
    this.tick();
  }

  pageView(page: string, params?: object) {
    if (this.is_storesprint_user === true || !shouldTrackPageViews) {
      return;
    }

    this._track("Mobile Page View", { ...params, page });
  }

  async flush() {
    if (this.state === "flushing" || this.queue.length === 0) {
      return;
    }
    this.state = "flushing";
    const thisBatch = this.queue.slice(0, 50);
    this.queue = this.queue.slice(50);

    let uptime: number;
    try {
      if (!deviceType) {
        deviceType = await Device.getDeviceTypeAsync();
      }
      if (!maxMem && Platform.OS === "android") {
        maxMem = await Device.getMaxMemoryAsync();
      }

      if (Platform.OS !== "web") {
        uptime = await Device?.getUptimeAsync();
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {}

    const options = {
      method: "POST",

      headers: { Accept: "text/plain", "Content-Type": "application/json" },
      body: JSON.stringify(
        thisBatch.map((b) => ({
          event: b.event,
          properties: {
            ...b.userProperties,
            storesprintSessionId: sessionId,
            session_id: Constants.sessionId,
            time: b.properties.time,
            $insert_id: b.properties.$insert_id,

            $os: Platform.OS,
            $device_id: this.device_id,
            $screen_height: screenHeight,
            $screen_width: screenWidth,
            $os_version: Device.osVersion,
            uptime,
            maxMem,
            distinct_id: b.properties.distinct_id,
            token: this.mixpanelToken,
            store: this.store_id,
            modelName: Device.modelName,
            brand: Device.brand,
            version: Constants.expoConfig?.version,

            device_type: deviceType,
            locale: Localization.getLocales()[0]?.languageCode,
          },
        }))
      ),
    };

    await fetch("https://api-eu.mixpanel.com/track?ip=1", options)
      .then((r) => {
        if (r.ok) {
          return r;
        }
        throw new Error("Failed to send to mixpanel");
      })
      .then((response) => response.json())

      .then(() => {
        console.log(`Sent mixpanel ${thisBatch.length} events`);
      })
      .catch((err) => {
        addBreadcrumb({
          category: "mixpanel",
          message: "Error",
          data: { error: err },
        });

        console.log(`Failed to send mixpanel events`);
        console.error(err);
        this.queue = [...this.queue, ...thisBatch];
      });

    this.state = "idle";
  }
}
